// // components/TurnstileWidget.tsx
import React, { useEffect, useRef } from "react";
import Config from "./../../Config";

declare global {
  interface Window {
    turnstile: any;
  }
}

type TurnstileProps = {
  onSuccess: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  /**
   * Change this to make the widget issue a fresh token.
   *
   * A Turnstile token can be verified once. Cloudflare rejects the second
   * attempt with `timeout-or-duplicate`, so any screen that can submit twice --
   * a failed sign-in retried, or "resend reset link" -- needs a new token for
   * the second submission. Without a way to ask for one the only way out was
   * to reload the page.
   */
  resetSignal?: number;
};

const TurnstileWidget: React.FC<TurnstileProps> = ({
  onSuccess,
  onError,
  onExpire,
  resetSignal = 0,
}) => {
  const widgetRef = useRef<HTMLDivElement>(null);
  const turnstileIdRef = useRef<string | null>(null);
  const isRenderedRef = useRef<boolean>(false);

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.turnstile &&
      widgetRef.current &&
      !isRenderedRef.current
    ) {
      turnstileIdRef.current = window.turnstile.render(widgetRef.current, {
        sitekey: Config.CLOUDFLARE_SITE_KEY,
        callback: onSuccess,
        theme: "light", // optional (dark or light)
        "error-callback": () => {
          console.error("Turnstile widget failed to verify.");
          onError?.();
        },
        "expired-callback": () => {
          // Token has expired — notify parent so it can require re-verification.
          onExpire?.();
        },
      });

      isRenderedRef.current = true;
    }
  }, [onSuccess, onError, onExpire]);

  // Skip the first run: the widget has just rendered and its token is unused.
  const lastResetRef = useRef<number>(resetSignal);
  useEffect(() => {
    if (resetSignal === lastResetRef.current) return;
    lastResetRef.current = resetSignal;
    if (turnstileIdRef.current && window.turnstile?.reset) {
      window.turnstile.reset(turnstileIdRef.current);
    }
  }, [resetSignal]);

  return <div ref={widgetRef} />;
};

export default TurnstileWidget;


